import type { ReservationInWindow } from '@/api/processStepsApi'
import { CGT_FORM_LABELS, type CgtFormType } from '@/types/cgt'
import {
  PROCESS_EVENT_TYPE_CODE,
  UNASSIGNED_LABEL,
  type MaintenanceTask,
} from '@/types/maintenanceTask'
import { localDayIso } from '@/lib/timeWindow'

// ─── Process-step reservations as worklist rows ──────────────────────────────
//
// A reserved machine is unavailable for exactly the same reason a machine in
// maintenance is, so the Tasks tab has to show both — otherwise a unit that is
// booked all afternoon reads as free there while the Equipment grid and the
// planner calendar both say it is taken.
//
// These rows are DERIVED, never persisted. Nothing here creates a
// MaintenanceEvent: the process step's own ProcessStepExecution stays the single
// start/end authority, so the mapping marks every row `readOnly` and gives it
// the `reserved` status rather than borrowing a maintenance lifecycle it does
// not have.
//
// Pure and dependency-free (no React, no network) so it can be unit-tested — the
// vitest config only picks up `src/**/*.test.ts` and runs in the node env.

/** Row ids are namespaced so a derived row can never collide with an event id. */
export const RESERVATION_ROW_PREFIX = 'reservation-'

const MS_PER_HOUR = 3_600_000

/** True for rows produced by `reservationToTask`. */
export function isReservationRow(taskId: string): boolean {
  return taskId.startsWith(RESERVATION_ROW_PREFIX)
}

/**
 * How far along the hold actually is — the ONE place that rule lives.
 *
 * The worklist and the Team Schedule both need it and their status vocabularies
 * differ (`completed` vs `done`), so this returns the phase and each surface
 * names it in its own terms. Deciding it twice is how they would drift.
 *
 * A hold used to report `reserved` for its whole life, which was true before
 * units carried an actual start and end. Now that they do, a machine someone is
 * standing at looks identical to one merely booked for later — the single thing
 * a technician scanning the board most needs to tell apart.
 */
export function reservationPhase(res: ReservationInWindow): 'reserved' | 'started' | 'finished' {
  if (res.actualEnd) return 'finished'
  if (res.actualStart) return 'started'
  return 'reserved'
}

/**
 * "Used By" for a booking is the procedure that booked it, which is the only thing a
 * technician looking at an occupied machine actually wants to know. Falls back
 * to the equipment type when the submission is gone, and to an em dash when
 * there is nothing true left to say.
 */
export function bookedFor(res: ReservationInWindow): string {
  const parts = [
    res.patientMrn,
    res.formType ? (CGT_FORM_LABELS[res.formType as CgtFormType] ?? res.formType) : null,
  ].filter((p): p is string => !!p)
  if (parts.length > 0) return parts.join(' · ')
  return res.equipmentTypeName ?? '—'
}

/** Duration of the hold in hours. Negative/invalid windows clamp to 0. */
export function reservationHours(startsAt: string, endsAt: string): number {
  const ms = new Date(endsAt).getTime() - new Date(startsAt).getTime()
  return Number.isFinite(ms) && ms > 0 ? ms / MS_PER_HOUR : 0
}

/**
 * Turn a recorded actor string into something worth reading in the Assigned
 * column. Given the roster, `reservationsToTasks` supplies one; without it
 * every row falls back to the raw string, as it always did.
 */
export type ActorNamer = (actor: string) => string | null

/** Map one live hold onto the worklist row shape. */
export function reservationToTask(
  res: ReservationInWindow,
  nameActor?: ActorNamer,
): MaintenanceTask {
  return {
    id: `${RESERVATION_ROW_PREFIX}${res.id}`,
    taskClass: 'process',
    // The unit's own label; the raw id is a cuid and means nothing to a reader,
    // but it beats showing an empty cell if the unit row is gone.
    equipmentId: res.equipmentLabel ?? res.equipmentId,
    usedBy: bookedFor(res),
    eventType: PROCESS_EVENT_TYPE_CODE,
    // A hold carries no priority. Inventing one (3, "normal") would sort these
    // rows against maintenance work as if someone had triaged them.
    priority: null,
    // Local day, not `startsAt.slice(0, 10)` (UTC): the row's clock times render
    // in local time, so a UTC day would put an evening booking on the day after
    // the times it displays — and disagree with every other surface.
    dueDate: localDayIso(res.startsAt),
    planHours: reservationHours(res.startsAt, res.endsAt),
    // `reservedBy` is the actor string the booking recorded, not a User id — so
    // it fills the Assigned column but must NOT become `assigneeId`, which the
    // Start control uses to decide whether work may begin.
    //
    // That string is `actorOf(user)` server-side, i.e. an EMAIL ADDRESS in
    // practice, which is exactly what the column was showing. Three sources,
    // most authoritative first:
    //
    //   1. `reservedByName` — resolved server-side against the whole User
    //      table, so it also names an actor who has since been disabled or who
    //      never was a member of this tenant.
    //   2. `nameActor` — the client's roster, which covers an older backend
    //      that sends no resolved name.
    //   3. the raw actor, because "booked by someone no longer on the roster"
    //      is still more use than "Unassigned" on a hold that plainly has an
    //      owner.
    assigned:
      (res.reservedBy &&
        (res.reservedByName?.trim() || nameActor?.(res.reservedBy) || res.reservedBy)) ||
      UNASSIGNED_LABEL,
    // `readOnly` is what refuses mutation, not the status — so reporting the
    // real phase here tells the truth without offering a control this worklist
    // does not own.
    status:
      reservationPhase(res) === 'finished'
        ? 'completed'
        : reservationPhase(res) === 'started'
          ? 'started'
          : 'reserved',
    readOnly: true,
    window: { startsAt: res.startsAt, endsAt: res.endsAt },
    unitOutOfService: res.equipmentStatus === 'OUT_OF_SERVICE',
  }
}

/** Map a window's worth of live holds. Released holds never reach this. */
export function reservationsToTasks(
  reservations: ReservationInWindow[],
  nameActor?: ActorNamer,
): MaintenanceTask[] {
  // Not `.map(reservationToTask)`: `Array.prototype.map` passes the INDEX as
  // the second argument, which would arrive here as the namer.
  return reservations.map((res) => reservationToTask(res, nameActor))
}
